import { computed, ref } from 'vue';

export type Theme = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

const STORAGE_KEY = 'sarraf:theme';

const currentTheme = ref<Theme>('system');
const effective = ref<EffectiveTheme>('dark');
let mediaListener: (() => void) | null = null;

function systemPreference(): EffectiveTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolve(theme: Theme): EffectiveTheme {
  return theme === 'system' ? systemPreference() : theme;
}

function apply(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const next = resolve(theme);
  effective.value = next;
  document.documentElement.classList.toggle('dark', next === 'dark');
  document.documentElement.style.colorScheme = next;
}

function attachSystemListener(): void {
  if (typeof window === 'undefined' || mediaListener) return;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mediaListener = (): void => {
    if (currentTheme.value === 'system') apply('system');
  };
  mq.addEventListener('change', mediaListener);
}

function detachSystemListener(): void {
  if (typeof window === 'undefined' || !mediaListener) return;
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .removeEventListener('change', mediaListener);
  mediaListener = null;
}

export function useTheme() {
  const setTheme = (theme: Theme): void => {
    currentTheme.value = theme;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, theme);
    }
    apply(theme);
    if (theme === 'system') attachSystemListener();
    else detachSystemListener();
  };

  const applyStoredTheme = (): void => {
    if (typeof localStorage === 'undefined') {
      apply('system');
      attachSystemListener();
      return;
    }
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    const initial: Theme = stored ?? 'system';
    currentTheme.value = initial;
    apply(initial);
    if (initial === 'system') attachSystemListener();
  };

  const cycleTheme = (): void => {
    const order: Theme[] = ['light', 'dark', 'system'];
    const next = order[(order.indexOf(currentTheme.value) + 1) % order.length] as Theme;
    setTheme(next);
  };

  return {
    theme: computed(() => currentTheme.value),
    effectiveTheme: computed(() => effective.value),
    isDark: computed(() => effective.value === 'dark'),
    setTheme,
    cycleTheme,
    applyStoredTheme,
  };
}

/**
 * Inline script (string) that sets the html.dark class BEFORE Vue mounts.
 * Eliminates the dark-to-light flash on cold load.
 */
export const NO_FLASH_SCRIPT = `
(function() {
  try {
    var stored = localStorage.getItem('${STORAGE_KEY}');
    var theme = stored || 'system';
    var effective = theme === 'system'
      ? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : theme;
    if (effective === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    document.documentElement.style.colorScheme = effective;
  } catch (e) {
    document.documentElement.classList.add('dark');
  }
})();
`;
